import { Button, Tooltip, SageTokens } from '@kajabi/sage-react';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
// TODO: Move these into common checkout
import { OfferStatus } from 'apps/commerce/popup_checkout/constants';
import { localizedPriceFromMinorUnit, readableTextColor } from '../util';
import useCheckoutText from '../hooks/useCheckoutText';

export const PayButton = ({
  dueNowPriceInCents,
  disableSubmit,
  isPaymentInProgress,
  buttonStyles,
  siteUserSignedIn,
  checkoutConfigData,
}) => {
  const { t } = useTranslation();
  const { getValues, watch } = useFormContext();
  const formValues = getValues();
  const { paymentMethods, checkoutPageColor, offerPurchase } = formValues;
  const isFree = watch('isFree');

  // Use the enhanced custom hook to handle checkout text override logic and button text calculation
  const { buttonText } = useCheckoutText(formValues, checkoutConfigData, {
    dueNowPriceInCents,
    isFree,
    t,
  });

  const shouldDisableButton =
    disableSubmit || offerPurchase?.type === 'subscription' || (!paymentMethods && !isFree);
  const shouldRenderTooltip = !paymentMethods && !isFree;

  // allow rendering button with non-disabled state when site user is logged in for previewing purposes
  const backgroundColor =
    shouldDisableButton && !siteUserSignedIn
      ? SageTokens.COLOR_PALETTE.GRAY_100
      : buttonStyles?.checkoutButtonBackgroundColor || checkoutPageColor;

  // Text color logic:
  // 1. If we're in preview mode (siteUserSignedIn=true), use the configured text color if available
  // 2. If button is disabled (and not in preview), use the default disabled text color
  // 3. Otherwise, use a readable color based on the background
  let textColor;
  if (siteUserSignedIn && buttonStyles?.checkoutButtonTextColor) {
    // Preview mode with configured text color
    textColor = buttonStyles.checkoutButtonTextColor;
  } else if (shouldDisableButton && !siteUserSignedIn) {
    // Disabled button in regular checkout
    textColor = SageTokens.COLOR_PALETTE.GREY_400;
  } else {
    // Enabled button
    textColor = readableTextColor(backgroundColor);
  }

  const payButtonStyles = {
    color: textColor,
    ...buttonStyles?.styles,
    backgroundColor,
  };

  // if there are no valid paymentMethods and the offer is not free,
  // show the disabled button with a tooltip
  return shouldRenderTooltip ? (
    <>
      <Tooltip.Element className="popup-submit-disabled" content={t('messages.payments_paused')} />
      <Button
        className="popup-submit-disabled"
        data-testid="popup-submit-button"
        type="submit"
        disabled={true}
        loading={false}
        small={buttonStyles?.checkoutButtonSize === 'small'}
        fullWidth={buttonStyles?.checkoutButtonSize === 'large'}
        style={payButtonStyles}
      >
        {buttonText}
      </Button>
    </>
  ) : (
    <Button
      data-testid="popup-submit-button"
      disabled={shouldDisableButton}
      fullWidth={buttonStyles?.checkoutButtonSize === 'large'}
      loading={isPaymentInProgress}
      small={buttonStyles?.checkoutButtonSize === 'small'}
      style={payButtonStyles}
      type="submit"
    >
      {buttonText}
    </Button>
  );
};

PayButton.propTypes = {
  dueNowPriceInCents: PropTypes.number.isRequired,
  disableSubmit: PropTypes.bool.isRequired,
  isPaymentInProgress: PropTypes.bool.isRequired,
  buttonStyles: PropTypes.shape({
    checkoutButtonSize: PropTypes.string,
    checkoutButtonTextColor: PropTypes.string,
    checkoutButtonBackgroundColor: PropTypes.string,
  }),
  siteUserSignedIn: PropTypes.bool,
  checkoutConfigData: PropTypes.shape({
    checkoutTextOverride: PropTypes.string,
    checkoutTextOverrideEnabled: PropTypes.bool,
  }),
};

PayButton.defaultProps = {
  siteUserSignedIn: false,
  buttonStyles: {
    checkoutButtonSize: 'large',
    checkoutButtonTextColor: SageTokens.COLOR_PALETTE.WHITE,
    checkoutButtonBackgroundColor: SageTokens.COLOR_PALETTE.BLACK,
  },
  checkoutConfigData: {},
};

export default PayButton;
